import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { DateRange } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import OrderOrInvoiceIdFreeTextFilter from './order-or-invoice-id-free-text-filter/order-or-invoice-id-free-text-filter.component';
import DateFromToFilter from '../../shared/date-from-to-filter/date-from-to-filter.component';
import OrderReviewSelectFilter from './order-review-select-filter/order-review-select-filter.component';
import MultiSelectFilter from 'components/shared/multi-select-filter/multi-select-filter.component';
import ClinicFilter from 'components/shared/clinic-filter.component';
import {
    OrderReviewFilterType,
    InvoiceDeliveryMethod,
    InvoiceType,
    OrderOnDate,
    OrderStatusMultiSelect,
    ReadyForInvoiceOptions,
} from 'models/invoice';
import { ProductionStatus } from 'models/order';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Organization } from 'models/organization';

type OrderReviewFilterProps = {
    onOrderReviewFilterChange: (orderReviewFilter: OrderReviewFilterType) => void;
    filters: OrderReviewFilterType;
};

const OrderReviewFilter: FC<OrderReviewFilterProps> = ({
    onOrderReviewFilterChange,
    filters,
}: OrderReviewFilterProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    const [orderReviewFilter, setOrderReviewFilter] = useState<OrderReviewFilterType>();

    useEffect(() => {
        setOrderReviewFilter(filters);
    }, [filters]);

    const handleOrderOrInvoiceIdChange = (orderIdOrInvoiceNumber: string) => {
        setOrderReviewFilter({ ...orderReviewFilter, orderIdOrInvoiceNumber });
    };
    const handleClinicSelectChange = (_: React.SyntheticEvent, clinic: Organization | null) => {
        setOrderReviewFilter({ ...orderReviewFilter, clinicOrganizationId: clinic?._id as string, clinic });
    };
    const handleOrderStatusSelectChange = (orderStatus: string[]) => {
        const filter: OrderReviewFilterType = { ...orderReviewFilter, orderStatus };
        if (!orderStatus.length) {
            delete filter.orderStatus;
        }
        setOrderReviewFilter(filter);
    };
    const handleInvoiceStatusSelectChange = (invoicingState: string) => {
        const filter: OrderReviewFilterType = {
            ...orderReviewFilter,
            invoicingState,
        };
        if (invoicingState === ReadyForInvoiceOptions.ALL) {
            delete filter.invoicingState;
        }
        setOrderReviewFilter(filter);
    };
    const handleInvoiceTypeSelectChange = (invoiceType: string) => {
        const filter: OrderReviewFilterType = { ...orderReviewFilter, invoiceType };
        if (invoiceType === InvoiceType.ALL) {
            delete filter.invoiceType;
        }
        setOrderReviewFilter(filter);
    };
    const handleInvoiceDeliveryMethodChange = (deliveryMethod: string) => {
        const filter: OrderReviewFilterType = { ...orderReviewFilter, deliveryMethod };
        if (deliveryMethod === InvoiceDeliveryMethod.ALL) {
            delete filter.deliveryMethod;
        }
        setOrderReviewFilter(filter);
    };
    const handleOrderOnDateChange = (dateFilterType: string) => {
        const filter: OrderReviewFilterType = { ...orderReviewFilter, dateFilterType };
        if (dateFilterType === OrderOnDate.NO_DATE_FILTER) {
            delete filter.dateFilterType;
            delete filter.dateFromTo;
        }
        setOrderReviewFilter(filter);
    };
    const handleDateChange = (date: DateRange<Date>) => {
        const updatedFilter = { ...orderReviewFilter, dateFromTo: date };
        setOrderReviewFilter(updatedFilter);
        const isDateRangeCleared = !date[0] && !date[1];
        if (isDateRangeCleared) {
            const { dateFromTo: _removed, ...filterWithoutDateRange } = updatedFilter;
            onOrderReviewFilterChange(filterWithoutDateRange);
        }
    };
    const handleProductionChange = (production: string) => {
        const filter: OrderReviewFilterType = { ...orderReviewFilter, production };
        if (production === ProductionStatus.ALL) {
            delete filter.production;
        }
        setOrderReviewFilter(filter);
    };

    const filter = () => {
        const filters = { ...orderReviewFilter };
        onOrderReviewFilterChange(filters);
    };

    return (
        <Fragment>
            <Grid container rowSpacing={1.5} columnSpacing={1}>
                <Grid item xs={2}>
                    <OrderOrInvoiceIdFreeTextFilter
                        value={orderReviewFilter?.orderIdOrInvoiceNumber || ''}
                        onOrderOrInvoiceIdChange={handleOrderOrInvoiceIdChange}
                    />
                </Grid>
                <Grid item xs={4}>
                    <ClinicFilter
                        selectedClinic={orderReviewFilter?.clinic}
                        onClinicFilterChange={handleClinicSelectChange}
                        showParents
                    />
                </Grid>
                <Grid item xs={2}>
                    <OrderReviewSelectFilter
                        optionSelected={orderReviewFilter?.invoicingState || ReadyForInvoiceOptions.ALL}
                        label={translate('orders:READY_FOR_INVOICE')}
                        options={Object.values(ReadyForInvoiceOptions)}
                        onOptionChange={handleInvoiceStatusSelectChange}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OrderReviewSelectFilter
                        optionSelected={orderReviewFilter?.invoiceType || InvoiceType.ALL}
                        label={translate('invoices:INVOICE_TYPE')}
                        options={Object.values(InvoiceType)}
                        onOptionChange={handleInvoiceTypeSelectChange}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OrderReviewSelectFilter
                        optionSelected={orderReviewFilter?.deliveryMethod || InvoiceDeliveryMethod.ALL}
                        label={translate('invoices:DELIVERY_METHOD')}
                        options={Object.values(InvoiceDeliveryMethod)}
                        onOptionChange={handleInvoiceDeliveryMethodChange}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OrderReviewSelectFilter
                        optionSelected={orderReviewFilter?.production || ProductionStatus.ALL}
                        label={translate('orders:PRODUCTION')}
                        options={Object.values(ProductionStatus)}
                        onOptionChange={handleProductionChange}
                        translationNamespace="orders"
                    />
                </Grid>
                <Grid item xs={2}>
                    <OrderReviewSelectFilter
                        optionSelected={orderReviewFilter?.dateFilterType || OrderOnDate.NO_DATE_FILTER}
                        label={translate('orders:FILTER_BY_DATE')}
                        options={Object.values(OrderOnDate)}
                        onOptionChange={handleOrderOnDateChange}
                    />
                </Grid>
                <Grid item xs={4}>
                    <DateFromToFilter
                        dateFromTo={orderReviewFilter?.dateFromTo || [null, null]}
                        dateFilterType={orderReviewFilter?.dateFilterType || OrderOnDate.NO_DATE_FILTER}
                        onDateChange={handleDateChange}
                    />
                </Grid>
                <Grid item xs={4}>
                    <MultiSelectFilter
                        optionSelected={orderReviewFilter?.orderStatus || []}
                        label={translate('orders:ORDER_STATUS')}
                        options={Object.values(OrderStatusMultiSelect)}
                        onOptionChange={handleOrderStatusSelectChange}
                        translationNamespace="orders"
                    />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <Button onClick={filter} size="medium" type="button" startIcon={<RefreshIcon />}>
                        {translate('common:REFRESH_LIST')}
                    </Button>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default OrderReviewFilter;
